import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MapPin, X, Search, Loader2, Plus } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * CoverageAreasInput - lets a provider add multiple cities/municipalities they serve.
 * Stored as an array of "City, Province" strings (distinct from the base pin).
 * Props: value (string[]), onChange (fn(string[]))
 */
export default function CoverageAreasInput({ value = [], onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/locations/search`, { params: { q: q.trim(), limit: 8 } });
      const seen = new Set();
      const cities = [];
      (res.data.locations || []).forEach((loc) => {
        const label = [loc.city, loc.province].filter(Boolean).join(', ');
        if (label && !seen.has(label.toLowerCase())) {
          seen.add(label.toLowerCase());
          cities.push(label);
        }
      });
      setResults(cities);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const addArea = (label) => {
    const clean = (label || '').trim();
    if (!clean) return;
    const exists = value.some((v) => v.toLowerCase() === clean.toLowerCase());
    if (!exists) onChange([...value, clean]);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const removeArea = (label) => {
    onChange(value.filter((v) => v !== label));
  };

  return (
    <div className="space-y-3" data-testid="coverage-areas-input">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {value.map((area) => (
            <span
              key={area}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-green-50 text-green-700 text-sm rounded-full border border-green-200"
              data-testid={`coverage-chip-${area}`}
            >
              <MapPin className="w-3.5 h-3.5" />
              {area}
              <button
                type="button"
                onClick={() => removeArea(area)}
                className="ml-0.5 rounded-full hover:bg-green-200 p-0.5"
                data-testid={`coverage-remove-${area}`}
                aria-label={`Remove ${area}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">No coverage areas yet. Search and add the cities you serve.</p>
      )}

      <div className="relative" ref={boxRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search a city or municipality…"
            className="w-full h-11 pl-9 pr-9 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="coverage-search-input"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500 animate-spin" />}
        </div>

        {open && (results.length > 0 || (query.trim().length >= 2 && !loading)) && (
          <div className="absolute z-20 mt-1 w-full bg-white rounded-xl shadow-lg border border-slate-200 max-h-56 overflow-y-auto" data-testid="coverage-suggestions">
            {results.map((r) => (
              <button
                type="button"
                key={r}
                onClick={() => addArea(r)}
                className="w-full text-left px-3 py-2.5 text-sm text-slate-700 hover:bg-amber-50 flex items-center gap-2 border-b border-slate-50 last:border-0"
                data-testid={`coverage-suggestion-${r}`}
              >
                <MapPin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                {r}
              </button>
            ))}
            {results.length === 0 && query.trim().length >= 2 && !loading && (
              <button
                type="button"
                onClick={() => addArea(query)}
                className="w-full text-left px-3 py-2.5 text-sm text-slate-600 hover:bg-amber-50 flex items-center gap-2"
                data-testid="coverage-add-custom"
              >
                <Plus className="w-3.5 h-3.5 text-amber-500" />
                Add "{query.trim()}"
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
